<template>
  <div class="compliance-portal">
    <div class="compliance-portal__card">
      <div v-if="loading" class="text-center py-5">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p class="mt-2">Loading forms...</p>
      </div>

      <div v-if="errorMessage" class="text-center py-5">
        <i class="fa fa-exclamation-triangle fa-2x" style="color: #f56c6c"></i>
        <p class="mt-2" style="color: #f56c6c">{{ errorMessage }}</p>
      </div>

      <div v-if="portalData && !errorMessage && !loading">
        <h2 class="compliance-portal__title">Compliance Forms</h2>
        <p class="compliance-portal__subtitle">For: <strong>{{ portalData.clientName }}</strong></p>

        <!-- Progress Bar -->
        <div class="compliance-portal__progress">
          <div class="compliance-portal__progress-text">
            {{ signedCount }} of {{ portalData.forms.length }} forms completed
          </div>
          <div class="compliance-portal__progress-bar">
            <div
              class="compliance-portal__progress-fill"
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
        </div>

        <!-- Form List -->
        <div class="compliance-portal__list">
          <div
            v-for="form in portalData.forms"
            :key="form.id"
            class="compliance-portal__form-row"
          >
            <div class="compliance-portal__form-info">
              <span
                class="compliance-portal__status-dot"
                :style="{ backgroundColor: statusColor(form.status) }"
              ></span>
              <span class="compliance-portal__form-name">{{ form.displayName }}</span>
            </div>
            <div class="compliance-portal__form-actions">
              <el-tag
                :type="statusTagType(form.status)"
                size="small"
                effect="dark"
              >
                {{ statusLabel(form.status) }}
              </el-tag>
              <span v-if="form.signedAt" class="compliance-portal__signed-date">
                {{ formatDate(form.signedAt) }}
              </span>
              <el-button
                v-if="form.status === 'signed'"
                size="small"
                @click="viewPdf(form)"
                :loading="form._loadingPdf"
              >
                <i class="fa fa-file-pdf"></i> View PDF
              </el-button>
              <el-button
                v-if="form.status !== 'signed'"
                type="primary"
                size="small"
                @click="signForm(form)"
              >
                Sign
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { complianceFormsService } from "@/serviceHandlers/complianceFormsService";
import dayjs from "dayjs";

export default {
  name: "CompliancePortal",
  data() {
    return {
      portalData: null,
      loading: true,
      errorMessage: "",
    };
  },
  computed: {
    signedCount() {
      if (!this.portalData?.forms) return 0;
      return this.portalData.forms.filter((f) => f.status === "signed").length;
    },
    progressPercent() {
      if (!this.portalData?.forms?.length) return 0;
      return Math.round((this.signedCount / this.portalData.forms.length) * 100);
    },
  },
  async mounted() {
    const token = this.$route.params.token;
    if (!token) {
      this.errorMessage = "Invalid portal link.";
      this.loading = false;
      return;
    }

    try {
      const role = this.$route.query.role;
      this.portalData = await complianceFormsService.getPortal(token, role ? { role } : undefined);
    } catch (error) {
      this.errorMessage = error.response?.data?.message || "Could not load the compliance portal.";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    statusColor(status) {
      const colors = {
        signed: "#67c23a",
        expiring: "#e6a23c",
        expired: "#f56c6c",
        pending: "#909399",
        missing: "#404040",
        partially_signed: "#e6a23c",
      };
      return colors[status] || "#909399";
    },
    statusTagType(status) {
      const types = {
        signed: "success",
        expiring: "warning",
        expired: "danger",
        pending: "info",
        missing: "info",
        partially_signed: "warning",
      };
      return types[status] || "info";
    },
    statusLabel(status) {
      const labels = {
        signed: "Signed",
        expiring: "Expiring",
        expired: "Expired",
        pending: "Pending",
        missing: "Missing",
        partially_signed: "Partially Signed",
      };
      return labels[status] || status;
    },
    formatDate(dateStr) {
      return dayjs(dateStr).format("MM/DD/YYYY");
    },
    signForm(form) {
      const portalToken = this.$route.params.token;
      const role = this.$route.query.role;
      const query = `portal=${portalToken}${role ? `&role=${role}` : ""}`;
      this.$router.push(`/sign/${form.token}?${query}`);
    },
    async viewPdf(form) {
      this.$set(form, "_loadingPdf", true);
      try {
        const blob = await complianceFormsService.getSignedPdf(form.token);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch {
        this.$notifyError("Could not load the signed PDF.");
      } finally {
        this.$set(form, "_loadingPdf", false);
      }
    },
  },
};
</script>

<style scoped>
.compliance-portal {
  display: flex;
  justify-content: center;
  padding: 2rem;
  min-height: 100vh;
  background: rgba(37, 55, 65, 1);
}
.compliance-portal__card {
  background: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
}
.compliance-portal__title {
  color: #fff;
  text-align: center;
  margin-bottom: 0.5rem;
}
.compliance-portal__subtitle {
  text-align: center;
  color: #b3a6a6;
  margin-bottom: 1.5rem;
}
.compliance-portal__progress {
  margin-bottom: 1.5rem;
}
.compliance-portal__progress-text {
  color: #e0e0e0;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}
.compliance-portal__progress-bar {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
}
.compliance-portal__progress-fill {
  background: #67c23a;
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}
.compliance-portal__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.compliance-portal__form-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}
.compliance-portal__form-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}
.compliance-portal__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.compliance-portal__form-name {
  color: #e0e0e0;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.compliance-portal__form-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  margin-left: 1rem;
}
.compliance-portal__signed-date {
  color: #b3a6a6;
  font-size: 0.8rem;
}
</style>
